import { Button, getKeyValue, Input, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import React, { useEffect, useRef, useState } from 'react'
import CustomDropdown from '../../customdropdown';
import { ActiveIcon, CopyIcon, InActiveIcon, LeftArrowIcon, RightArrowIcon, SearchIcon, SortDownIcon, ViewListIcon } from '@/assets/icons/admin';
import ViewListEyeIcon from '@/assets/icons/admin/referdownline/viewlisteye';
import AddRefList from './addReferral';
import RefPlayerDetailTab from './playerDetails';
import AddNewRef from './addNewRef';
import { SpecialReferralDetails } from '@/types';
import { useGetSpecialReferrals } from '@/services/admin.service';
import { ArrayItems } from '../../customdropdown/dropdownarray';

export default function SpecialReferral() {
  const [datePlaceholder, setDatePlaceholder] = useState("DD/MM/YY");
  const inputRef = useRef(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedTier, setSelectedTier] = useState<string>("all");
  const [searchField, setSearchField] = useState<string>("all");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleStatusChange = (selectedOption: ArrayItems | null) => {
    if (selectedOption) {
      setSelectedStatus(selectedOption.value); // Ensure value is a string
    } else {
      setSelectedStatus("all"); // Default value if needed
    }
  };

  const handleTierChange = (selectedOption: ArrayItems | null) => {
    if (selectedOption) {
      setSelectedTier(selectedOption.value); // Ensure value is a string
    } else {
      setSelectedTier("all"); // Default value if needed
    }
  };

  const handleSearchFieldChange = (selectedOption: ArrayItems | null) => {
    if (selectedOption) {
      setSearchField(selectedOption.value); // Ensure value is a string
    } else {
      setSearchField("all"); // Default value if needed
    }
  };

  const handleSearchKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };


  const handleFocus = (e: any) => {
    e.currentTarget.type = "date";
  };

  const handleBlur = (e: any) => {
    e.currentTarget.type = "text";
    e.currentTarget.placeholder = datePlaceholder;
  };

  const statuses = [
    { label: "All", value: "all" },
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ];

  const tiers = [
    { label: "All", value: "all" },
    { label: "Tier 1", value: "Tier 1" },
    { label: "Tier 2", value: "Tier 2" },
    { label: "Tier 3", value: "Tier 3" },
  ];

  const fields = [
    { label: "All", value: "all" },
    { label: "Received Date", value: "createdAt" },
    { label: "Username", value: "userName" },
    { label: "Fullname", value: "fullName" },
    { label: "Ref.Links", value: "refLink" },
    { label: "Special Rates (%)", value: "specialRate" },
    { label: "Tier", value: "tierLevel" },
    { label: "Job Title", value: "job" },
  ]

  const columns = [
    {
      key: "no",
      label: "No",
    },
    {
      key: "createdAt",
      label: "Received Date",
    },
    {
      key: "userName",
      label: "Username",
    },
    {
      key: "fullName",
      label: "Fullname",
    },
    {
      key: "refLink",
      label: "Ref.Links",
    },
    {
      key: "specialRate",
      label: "Special Rates (%)",
    },
    {
      key: "tierLevel",
      label: "Tier",
    },
    {
      key: "job",
      label: "Job Title",
    },
    {
      key: "status",
      label: "Status",
    },
    {
      key: "action",
      label: "Action",
    },
  ]

  const [tableRows, setTableRows] = useState<SpecialReferralDetails[]>([]);
  const { data: getAllHistory } = useGetSpecialReferrals({ page: 1, pageSize: 10 });

  useEffect(() => {
    if (getAllHistory?.data?.data.length > 0) {
      setTableRows(getAllHistory?.data?.data);
      console.log("getAllHistory : ", getAllHistory?.data?.data);
    }
  }, [getAllHistory?.data?.data]);

  const handleCopyText = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("Text copied to clipboard:", text);
      })
      .catch((error) => {
        console.error("Error copying text to clipboard:", error);
      });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPageOptions = ["All", "3", "5", "10"];
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleClickNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleClickPrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const applyFilters = () => {
    let filteredRows = tableRows;

    // Apply date filter
    if (startDate) {
      filteredRows = filteredRows.filter(row => new Date(row.createdAt) == new Date(startDate));
    }
    if (endDate) {
      filteredRows = filteredRows.filter(row => new Date(row.createdAt) <= new Date(endDate));
    }

    // Apply status filter
    if (selectedStatus !== "all") {
      filteredRows = filteredRows.filter(row => row.status === selectedStatus);
    }

    // Apply tier filter
    if (selectedTier !== "all") {
      filteredRows = filteredRows.filter(row => row.tierLevel === selectedTier);
    }

    // Apply search filter
    if (searchKeyword) {
      const lowerCaseSearchKeyword = searchKeyword.toLowerCase();
      filteredRows = filteredRows.filter(row => {
        if (searchField === "all") {
          return Object.values(row).some(value => value.toString().toLowerCase().includes(lowerCaseSearchKeyword));
        }
        const fieldValue = row[searchField as keyof typeof row]?.toString().toLowerCase();
        return fieldValue?.includes(lowerCaseSearchKeyword);
      });
    }

    // Apply sorting
    if (sortColumn) {
      filteredRows.sort((a, b) => {
        const aValue = a[sortColumn as keyof typeof a];
        const bValue = b[sortColumn as keyof typeof b];
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredRows;
  };

  let paginatedRows = applyFilters();

  if (rowsPerPage !== "All") {
    paginatedRows = paginatedRows.slice(
      (currentPage - 1) * parseInt(rowsPerPage),
      currentPage * parseInt(rowsPerPage)
    );
  }

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      // Toggle sort direction if the same column is clicked
      setSortDirection(prevDirection => (prevDirection === 'asc' ? 'desc' : 'asc'));
    } else {
      // Set new column and default to ascending
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };


  const totalRows = tableRows.length;
  const startRow = (currentPage - 1) * paginatedRows.length + 1;
  const endRow = Math.min(currentPage * paginatedRows.length, totalRows);

  const rowsRange = `${startRow}-${endRow} out of ${totalRows}`;

  const [isAddingRef, setIsAddingRef] = useState(false);
  const handleAddRefClick = () => {
    setIsAddingRef(true);
  };

  const handleCancelAddRef = () => {
    setIsAddingRef(false);
  };

  const [isEditingMultiple, setIsEditingMultiple] = useState(false);
  const handleEditMultipleClick = () => {
    setIsEditingMultiple(true);
  };

  const handleCancelEditMultiple = () => {
    setIsEditingMultiple(false);
  };

  const [isEditingRef, setIsEditingRef] = useState(false);
  const [detailReferralData, setDetailReferralData] = useState<SpecialReferralDetails>();

  const handleEditRefClick = (itemData: any) => {
    setIsEditingRef(true);
    setDetailReferralData(itemData)
  };

  const handleCancelEditRef = () => {
    setIsEditingRef(false);
    setDetailReferralData(undefined)
  };

  const formatDateTime = (isoString: string | number | Date) => {
    const date = new Date(isoString);

    const padZero = (num: number) => (num < 10 ? "0" : "") + num;

    const day = padZero(date.getDate());
    const month = padZero(date.getMonth() + 1); // getMonth() returns month from 0 to 11
    const year = date.getFullYear(); // Get full year
    const hours = padZero(date.getHours());
    const minutes = padZero(date.getMinutes());
    const seconds = padZero(date.getSeconds()); // Add this line to get seconds

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; // Include seconds in the returned string
  };

  return (
    <div>
      {
        isAddingRef ? (
          <AddNewRef onCancel={handleCancelAddRef} />
        ) : isEditingMultiple ? (
          <AddRefList onCancel={handleCancelEditMultiple} />
        ) : isEditingRef ? (
          <RefPlayerDetailTab item={detailReferralData!} onCancel={handleCancelEditRef} />
        ) : (
          <div className="">
            <div className="flex flex-col sm:flex-row items-start justify-between">
              <div className="font-ubuntu text-left text-2xl font-semibold text-white mb-4 sm:mb-0">
                <p className="text-sm sm:text-xl">
                  Special Referral
                </p>
              </div>
              <div className="w-full sm:w-auto sm:ml-4">
                <Button
                  onClick={handleAddRefClick}
                  className="admin-common-button-gradient font-ubuntu w-full sm:full rounded-md text-base font-bold"
                >
                  Add Special Referral
                </Button>
              </div>
            </div>
            <div className="flex w-full flex-col gap-4">
              <div className="mb-6 mt-5 flex w-auto flex-wrap items-start gap-3 sm:gap-4 md:mb-0 md:flex-nowrap">
                <div className='w-[48%] sm:w-[15%] flex flex-col'>
                  <span className='font-ubuntuMono font-bold text-base sm:text-lg'>Start End</span>
                  <input
                    type="text"
                    name="expirationdate"
                    value={startDate}
                    onChange={handleStartDateChange}
                    ref={inputRef}
                    placeholder={datePlaceholder}
                    className="input-bg w-full h-10 px-3 cursor-text text-[13px]"
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
                <div className='w-[48%] sm:w-[15%] flex flex-col'>
                  <span className='font-ubuntuMono font-bold text-base sm:text-lg'>End Date</span>
                  <input
                    type="text"
                    name="expirationdate"
                    value={endDate}
                    onChange={handleEndDateChange}
                    ref={inputRef}
                    placeholder={datePlaceholder}
                    className="input-bg w-full h-10 px-3 cursor-text text-[13px]"
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
                <div className='w-[48%] sm:w-[15%]'>
                  <span className=' font-ubuntuMono font-bold text-base sm:text-lg'>Status</span>
                  <div className="sm:w-full">
                    <CustomDropdown
                      options={statuses}
                      placeholder="All"
                      onSelect={handleStatusChange}
                      value={selectedStatus}
                    />
                  </div>
                </div>
                <div className='w-[48%] sm:w-[15%]'>
                  <span className=' font-ubuntuMono font-bold text-base sm:text-lg'>Tier</span>
                  <div className="sm:w-full">
                    <CustomDropdown
                      options={tiers}
                      placeholder="All"
                      onSelect={handleTierChange}
                      value={selectedTier}
                    />
                  </div>
                </div>
                <div className='w-[48%] sm:w-[15%]'>
                  <span className=' font-ubuntuMono font-bold text-base sm:text-lg'>Search By</span>
                  <div className="sm:w-full">
                    <CustomDropdown
                      options={fields}
                      placeholder="All"
                      onSelect={handleSearchFieldChange}
                      value={searchField}
                    />
                  </div>
                </div>
                <div className='w-[48%] sm:w-[15%] mt-[23px] sm:mt-[26px]'>
                  <span className=' font-ubuntuMono font-bold text-base sm:text-lg'>{""}</span>
                  <Input
                    className="input-bg w-full"
                    isClearable
                    classNames={{
                      input: [
                        "mr-0",
                        "text-white/90 dark:text-white/90",
                        "dark:placeholder:text-white/60 ml-2",
                      ],
                      inputWrapper: [
                        "shadow-xl",
                        "!cursor-text",
                        "bg-white-5",
                        "text-[13px]"
                      ],
                    }}
                    placeholder="Search Keyword"
                    startContent={<SearchIcon />}
                    value={searchKeyword}
                    onChange={handleSearchKeywordChange}
                  ></Input>
                </div>
                <div className='w-[40%] sm:w-[15%] mt-[-5px] sm:mt-[26px]'>
                  <span className=' font-ubuntuMono font-bold text-base sm:text-lg'>{""}</span>
                  <Button className="admin-common-button-gradient font-ubuntu w-full rounded-md text-base font-bold">
                    Apply
                  </Button>
                </div>
              </div>
            </div>
            <div className="w-full sm:mt-5">
              <div className="w-full overflow-x-auto">
                <Table removeWrapper className="table-striped table table-striped-even">
                  <TableHeader className="rounded-none" columns={columns}>
                    {(column) => (
                      <TableColumn key={column.key}>
                        <div className="flex white-op">
                          <span
                            onClick={column.label === "No" ? () => handleEditMultipleClick() : undefined}
                            className={column.label === "No" ? "cursor-pointer" : ""}
                          >
                            {column.label}
                          </span>
                          <Button
                            isIconOnly
                            className="transparent-bg h-auto w-auto"
                            onClick={() => handleSort(column.key)}
                          >
                            <SortDownIcon />
                          </Button>
                        </div>
                      </TableColumn>
                    )}
                  </TableHeader>
                  <TableBody items={paginatedRows}>
                    {(item) => (
                      <TableRow key={item.id}>
                        {(columnKey) => (
                          <TableCell>
                            {columnKey === "no" ? (
                              <div className="font-openSans text-sm font-bold">
                                {" "}
                                {tableRows.findIndex(
                                  (row) => (row as any)?.id === (item as any)?.id,
                                ) +
                                  1 +
                                  "."}
                              </div>
                            ) : columnKey === "userName" ? (
                              <div className="font-bold tb-data-blue">
                                {" "}
                                {getKeyValue(item, columnKey)}
                              </div>
                            ) : columnKey === "createdAt" ? (
                              <div className="w-max">
                                <div>{formatDateTime(item?.createdAt)}</div>
                              </div>
                            ) : columnKey === "status" ? (
                              <div className="flex items-center">
                                {item.status === "ACTIVE" ? (
                                  <span>
                                    <ActiveIcon />
                                  </span>
                                ) : (
                                  <span>
                                    <InActiveIcon />
                                  </span>
                                )}
                              </div>
                            ) : columnKey === "specialRate" ? (
                              <div className="text-right mr-10 font-bold tb-data-blue">
                                {" "}
                                {getKeyValue(item, columnKey) + "%"}
                              </div>
                            ) : columnKey === "tierLevel" ? (
                              <div className="font-bold">
                                {" "}
                                {getKeyValue(item, columnKey)}
                              </div>
                            ) : columnKey === "refLink" ? (
                              <div className="items-center font-normal">
                                <span className="flex items-center">
                                  <p>{getKeyValue(item, columnKey)}</p>
                                  <Button
                                    isIconOnly
                                    onClick={() => handleCopyText(getKeyValue(item, columnKey))}
                                    className="transparent-bg h-6 w-6"
                                  >
                                    <CopyIcon />
                                  </Button>
                                </span>
                              </div>
                            ) : columnKey === "action" ? (
                              <div>
                                <Button
                                  onClick={() => { handleEditRefClick(item) }}
                                  isIconOnly
                                  className="transparent-bg h-auto w-auto"
                                >
                                  <ViewListEyeIcon />
                                </Button>
                              </div>
                            ) : (
                              <div className="font-normal">
                                {" "}
                                {getKeyValue(item, columnKey)}
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-1 flex	items-center table-striped-even text-sm sm:justify-end sm:text-base">
                <div className="ml-[15px] justify-start sm:ml-0 sm:ml-4 sm:mr-8">
                  <label
                    htmlFor="rowsPerPage"
                    className="font-sans opacity-30 sm:mr-2"
                  >
                    Rows per page:
                  </label>
                  <select
                    id="rowsPerPage"
                    className="select-menu font-open-sans rounded border-none bg-transparent px-2 py-1 font-normal text-gray-500"
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <option
                        key={option}
                        value={option}
                        className="font-open-sans text-gray-600 sm:font-normal"
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex">
                  <div className="ml-[8px] mt-2 opacity-30 sm:ml-[45px] sm:mr-8">
                    <p>{rowsRange}</p>
                  </div>
                  <button
                    onClick={handleClickPrevPage}
                    className="ml-[18px] mr-2 text-xs sm:ml-0 sm:mr-3"
                  >
                    <LeftArrowIcon />
                  </button>
                  <button onClick={handleClickNextPage} className="text-xs">
                    <RightArrowIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div>
  )
}
